import { Fragment, type ReactNode } from 'react';

import { cx } from '../../lib/cx';

import styles from './DataTable.module.css';

export interface DataColumn<Row> {
  id: string;
  header: ReactNode;
  cell: (row: Row) => ReactNode;
  /** The column that names the row: its cells are the headers of their rows. */
  primary?: boolean;
  align?: 'start' | 'end';
  /** A CSS width, for a column that should not take what is left: "12rem", "20%". */
  width?: string;
}

export interface DataTableProps<Row> {
  /** What the table lists. Its accessible name; shown only when `showCaption` is set. */
  caption: string;
  showCaption?: boolean;
  columns: readonly DataColumn<Row>[];
  rows: readonly Row[];
  rowKey: (row: Row) => string;
  /** More about a row, across the whole width under it; nothing for rows without it. */
  detail?: (row: Row) => ReactNode;
  /** Shown in place of the rows when there are none. */
  empty?: ReactNode;
  /** Rows closer together, for long lists. */
  dense?: boolean;
  className?: string;
}

/**
 * Records in rows and their fields in columns: what a person compares and scans.
 * A table wider than its place scrolls inside it, and the page does not; the scrolling
 * part can be focused, so it is reachable from the keyboard.
 */
export function DataTable<Row>({
  caption,
  showCaption = false,
  columns,
  rows,
  rowKey,
  detail,
  empty,
  dense = false,
  className,
}: DataTableProps<Row>) {
  return (
    <div role="region" aria-label={caption} tabIndex={0} className={cx(styles.scroll, className)}>
      <table className={cx(styles.table, dense && styles.dense)}>
        <caption className={showCaption ? styles.caption : styles.hidden}>{caption}</caption>
        <thead>
          <tr>
            {columns.map((column) => (
              <th
                key={column.id}
                scope="col"
                className={cx(styles.head, column.align === 'end' && styles.end)}
                style={column.width ? { width: column.width } : undefined}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={styles.empty}>
                {empty}
              </td>
            </tr>
          ) : null}
          {rows.map((row) => {
            const more = detail?.(row);
            const hasMore = more !== null && more !== undefined && more !== false;
            return (
              <Fragment key={rowKey(row)}>
                <tr className={cx(styles.row, hasMore && styles.withDetail)}>
                  {columns.map((column) =>
                    column.primary ? (
                      <th key={column.id} scope="row" className={cx(styles.cell, styles.primary)}>
                        {column.cell(row)}
                      </th>
                    ) : (
                      <td key={column.id} className={cx(styles.cell, column.align === 'end' && styles.end)}>
                        {column.cell(row)}
                      </td>
                    ),
                  )}
                </tr>
                {hasMore ? (
                  <tr className={styles.detail}>
                    <td colSpan={columns.length} className={styles.cell}>
                      {more}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
